import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_URL } from "../config";
import { MathfieldElement } from "mathlive";
import { BlockMath, InlineMath } from "react-katex";
import "katex/dist/katex.min.css";
import "mathlive";
import "../css/mathlive.core.css";
import UploadExamObjectivePDF from "./GeneralQuestionsObjectivePDF";
import { Box, Typography } from "@mui/material";
import {
  alpha,
  createTheme,
  ThemeProvider,
  styled,
  keyframes,
  Paper,
  Button,
  TextField,
  Chip,
  Stack,
  Avatar,
  InputAdornment,
  Pagination,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ImageIcon from "@mui/icons-material/Image";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TuneIcon from "@mui/icons-material/Tune";
import CheckIcon from "@mui/icons-material/Check";
import FunctionsIcon from "@mui/icons-material/Functions";
import TextFieldsIcon from "@mui/icons-material/TextFields";

import UploadFileIcon from "@mui/icons-material/UploadFile";

// ─── Theme ────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#2563EB", light: "#DBEAFE", dark: "#1D4ED8" },
    secondary: { main: "#C4922A", light: "#FEF3C7", dark: "#92680C" },
    error: { main: "#DC2626", light: "#FEE2E2" },
    background: { default: "#F0F2F8", paper: "#FFFFFF" },
    text: { primary: "#0F1117", secondary: "#4B5563", disabled: "#9CA3AF" },
  },
  typography: {
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    h2: { fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
});

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// ─── Styled Components ────────────────────────────────────────────────────────
const PageWrapper = styled(Box)(() => ({
  minHeight: "100vh",
  background: "linear-gradient(145deg, #EEF2FF 0%, #F0F2F8 40%, #E8EDF8 100%)",
  padding: "28px 20px 60px",
  fontFamily: "'Sora', sans-serif",
  "&::before": {
    content: '""',
    position: "fixed",
    top: -120,
    right: -120,
    width: 480,
    height: 480,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(37,99,235,0.06) 0%, transparent 70%)",
    pointerEvents: "none",
  },
}));

const ContentBox = styled(Box)({ maxWidth: 880, margin: "0 auto" });

const SectionCard = styled(Paper, {
  shouldForwardProp: (p) => p !== "delay",
})(({ theme, delay }) => ({
  borderRadius: 16,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 8px 24px rgba(0,0,0,0.04)",
  padding: "24px 28px",
  marginBottom: 20,
  animation: `${fadeUp} 0.5s ease both`,
  animationDelay: `${delay ?? 0}ms`,
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(37,99,235,0.06), 0 12px 32px rgba(0,0,0,0.06)",
  },
}));

const CardLabel = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.9px",
  color: theme.palette.text.disabled,
  marginBottom: 18,
  display: "flex",
  alignItems: "center",
  gap: 8,
  "&::before": {
    content: '""',
    display: "inline-block",
    width: 3,
    height: 14,
    background: theme.palette.primary.main,
    borderRadius: 2,
  },
}));


const ModeToggle = styled(Box)(({ theme }) => ({
  display: "flex",
  background: alpha(theme.palette.primary.main, 0.06),
  borderRadius: 10,
  padding: 3,
  gap: 2,
}));

const ModeBtn = styled(Button, {
  shouldForwardProp: (p) => p !== "active",
})(({ theme, active }) => ({
  minWidth: 80,
  padding: "6px 16px",
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 700,
  textTransform: "none",
  boxShadow: "none",
  transition: "all 0.18s ease",
  ...(active
    ? {
        background: "#fff",
        color: theme.palette.primary.main,
        boxShadow: "0 1px 6px rgba(0,0,0,0.10)",
        "&:hover": {
          background: "#fff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        },
      }
    : {
        background: "transparent",
        color: theme.palette.text.disabled,
        "&:hover": {
          background: alpha("#fff", 0.5),
          color: theme.palette.text.secondary,
        },
      }),
}));

const PrimaryBtn = styled(Button)(({ theme }) => ({
  background: "linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)",
  color: "#fff",
  borderRadius: 10,
  padding: "11px 28px",
  fontSize: 14,
  fontWeight: 700,
  textTransform: "none",
  boxShadow: "0 4px 14px rgba(37,99,235,0.30)",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #1D4ED8 0%, #1E3A8A 100%)",
    boxShadow: "0 6px 20px rgba(37,99,235,0.40)",
    transform: "translateY(-1px)",
  },
  "&:active": { transform: "translateY(0)" },
  "&.Mui-disabled": {
    background: "#E5E7EB",
    color: "#9CA3AF",
    boxShadow: "none",
  },
}));

const BackBtn = styled(Button)(({ theme }) => ({
  borderRadius: 40,
  padding: "7px 18px 7px 12px",
  fontSize: 13,
  fontWeight: 600,
  textTransform: "none",
  color: theme.palette.text.secondary,
  border: `1px solid ${alpha("#000", 0.12)}`,
  background: "#fff",
  transition: "all 0.2s ease",
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.05),
    borderColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    transform: "translateX(-2px)",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    fontFamily: "'Sora', sans-serif",
    fontSize: 14,
    borderRadius: 10,
    background: alpha(theme.palette.background.default, 0.8),
    transition: "all 0.2s ease",
    "& fieldset": { borderColor: alpha("#000", 0.1), borderWidth: 1 },
    "&:hover fieldset": {
      borderColor: alpha(theme.palette.primary.main, 0.35),
    },
    "&.Mui-focused": {
      background: "#fff",
      "& fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 1.5,
        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.08)}`,
      },
    },
  },
  "& .MuiInputLabel-root": {
    fontFamily: "'Sora', sans-serif",
    fontSize: 13,
    color: theme.palette.text.disabled,
  },
}));

const QuestionItem = styled(Box, {
  shouldForwardProp: (p) => p !== "itemdelay",
})(({ theme, itemdelay }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: 14,
  padding: "16px 0",
  borderBottom: `1px solid ${alpha("#000", 0.06)}`,
  transition: "background 0.2s ease",
  animation: `${fadeUp} 0.4s ease both`,
  animationDelay: `${itemdelay ?? 0}ms`,
  borderRadius: 10,
  "&:last-child": { borderBottom: "none" },
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.025),
    paddingLeft: 8,
    paddingRight: 8,
  },
}));

const QNumber = styled(Avatar)(({ theme }) => ({
  width: 34,
  height: 34,
  borderRadius: 9,
  background: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  fontSize: 13,
  fontWeight: 800,
  fontFamily: "'Sora', sans-serif",
  flexShrink: 0,
}));

const ActionBtn = styled(Button, {
  shouldForwardProp: (p) => p !== "btncolor",
})(({ theme, btncolor }) => {
  const map = {
    option: {
      bg: alpha(theme.palette.primary.main, 0.08),
      color: theme.palette.primary.main,
      hbg: theme.palette.primary.main,
    },
    edit: { bg: alpha("#C4922A", 0.1), color: "#C4922A", hbg: "#C4922A" },
    delete: {
      bg: alpha(theme.palette.error.main, 0.08),
      color: theme.palette.error.main,
      hbg: theme.palette.error.main,
    },
  };
  const s = map[btncolor] || map.option;
  return {
    minWidth: "unset",
    padding: "5px 12px",
    borderRadius: 7,
    fontSize: 11,
    fontWeight: 700,
    textTransform: "none",
    background: s.bg,
    color: s.color,
    transition: "all 0.18s ease",
    "&:hover": {
      background: s.hbg,
      color: "#fff",
      transform: "translateY(-1px)",
      boxShadow: `0 4px 10px ${alpha(s.hbg, 0.3)}`,
    },
  };
});

const Spinner = styled(Box)(() => ({
  width: 20,
  height: 20,
  border: "2px solid rgba(37,99,235,0.15)",
  borderTop: "2px solid #2563EB",
  borderRadius: "50%",
  animation: `${spin} 0.7s linear infinite`,
  display: "inline-block",
}));

// ─── Constants ────────────────────────────────────────────────────────────────
const QUESTIONS_PER_PAGE = 10;

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AdminExamQuestionPage() {
  const { testId } = useParams();
  const [mode, setMode] = useState("text");
  const navigate = useNavigate();
  const imageInputRef = useRef(null);
  const questionRef = useRef(null);
  const [number, setNumber] = useState("");
  const [text, setText] = useState("");
  const [imageUri, setImageUri] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImage, setExistingImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isRemovingBg, setIsRemovingBg] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [assignedQuestions, setAssignedQuestions] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [testInfo, setTestInfo] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  if (!customElements.get("math-field")) {
    customElements.define("math-field", MathfieldElement);
  }

  const fetchQuestions = useCallback(() => {
    fetch(`${API_URL}/admin/exam/questions/${testId}/`)
      .then((res) => res.json())
      .then((data) => {
        setAssignedQuestions(data.assigned_questions || []);
      })
      .catch(() => alert("Failed to fetch questions."));
  }, [testId]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);


  const fetchTestInfo = useCallback(() => {
    fetch(`${API_URL}/admin/exam/test/${testId}/`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch test info");
        return res.json();
      })
      .then((data) => {
        setTestInfo(data); // contains level + year
      })
      .catch((err) => console.error(err));
  }, [testId]);

  useEffect(() => {
    fetchQuestions();
    fetchTestInfo(); // 👈 add this
  }, [fetchQuestions, fetchTestInfo]);

  // Filter + sort ascending by number
  const filteredAssigned = assignedQuestions
    .filter((q) => !searchQuery || q.number?.toString().includes(searchQuery))
    .sort((a, b) => Number(a.number) - Number(b.number));

  // Pagination calculations
  const totalPages = Math.ceil(filteredAssigned.length / QUESTIONS_PER_PAGE);
  const paginatedQuestions = filteredAssigned.slice(
    (currentPage - 1) * QUESTIONS_PER_PAGE,
    currentPage * QUESTIONS_PER_PAGE
  );

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsRemovingBg(true);
    try {
      const cleanedBlob = await removeBackgroundWithRemoveBg(file);
      const cleanedFile = new File([cleanedBlob], "cleaned.png", {
        type: "image/png",
      });
      setImageUri(cleanedFile);
      setImagePreview(URL.createObjectURL(cleanedFile));
      setExistingImage(null);
    } catch {
      alert("Background removal failed. Using original image.");
      setImageUri(file);
      setImagePreview(URL.createObjectURL(file));
      setExistingImage(null);
    } finally {
      setIsRemovingBg(false);
    }
  };

  const removeBackgroundWithRemoveBg = async (file) => {
    const formData = new FormData();
    formData.append("image_file", file);
    formData.append("size", "auto");
    const res = await fetch("https://api.remove.bg/v1.0/removebg", {
      method: "POST",
      headers: { "X-Api-Key": "hxRA6YNfZzrnsXgMYv9ZBYCh" },
      body: formData,
    });
    if (!res.ok) {
      const errorText = await res.text();
      throw new Error(`Remove.bg error: ${errorText}`);
    }
    return await res.blob();
  };

  const resetForm = () => {
    setNumber("");
    setText("");
    setImageUri(null);
    setImagePreview(null);
    setExistingImage(null);
    setEditMode(false);
    setEditId(null);
    questionRef.current?.setValue("");
    imageInputRef.current && (imageInputRef.current.value = "");
  };

  const handleSubmit = async () => {
    if (!number || !text) {
      alert("Question number and text are required.");
      return;
    }
    setLoading(true);
    const formData = new FormData();
    formData.append("number", number);
    formData.append("text", text);
    if (imageUri) formData.append("Eq_img", imageUri);

    const url = editMode
      ? `${API_URL}/admin/exam/question/${editId}/`
      : `${API_URL}/admin/exam/questions/${testId}/`;

    try {
      const res = await fetch(url, {
        method: editMode ? "PUT" : "POST",
        body: formData,
      });
      if (!res.ok) throw new Error(await res.text());
      const updatedQuestion = await res.json();
      if (updatedQuestion.class_term) {
        setAssignedQuestions((prev) => {
          const filtered = prev.filter((q) => q.id !== updatedQuestion.id);
          return [updatedQuestion, ...filtered];
        });
        setAssignedQuestions((prev) =>
          prev.filter((q) => q.id !== updatedQuestion.id)
        );
      }
      resetForm();
      fetchQuestions();
    } catch (err) {
      alert(
        editMode
          ? `Failed to update question.\n${err.message}`
          : `Failed to add question.\n${err.message}`
      );
    } finally {
      setLoading(false);
    }
  };

  const deleteQuestion = async (questionId) => {
    if (!window.confirm("Are you sure you want to delete this question?"))
      return;
    try {
      const res = await fetch(`${API_URL}/admin/exam/question/${questionId}/`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Delete failed");
      fetchQuestions();
    } catch {
      alert("Failed to delete question.");
    }
  };

  const startEdit = (questionId) => {
    fetch(`${API_URL}/admin/exam/question/${questionId}/`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed");
        return res.json();
      })
      .then((data) => {
        setNumber(data.number?.toString() || "");
        setText(data.text || "");
        setExistingImage(data.Eq_img || null);
        setEditMode(true);
        setEditId(data.id);
        setTimeout(() => {
          questionRef.current?.setValue(data.text || "");
        }, 0);
        window.scrollTo({ top: 0, behavior: "smooth" });
      })
      .catch(() => alert("Failed to fetch question details."));
  };

  const navigateToOption = (questionId) => {
    navigate(`/admin-exam-options/${questionId}`);
  };


  const renderContent = (text) => {
    if (!text) return null;

    const parts = text.split(
      /(\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\\\([\s\S]+?\\\)|\$[^$]+\$)/g
    );

    return parts.map((part, index) => {
      // $$...$$ (block)
      if (part.startsWith("$$") && part.endsWith("$$")) {
        return (
          <div key={index} style={{ margin: "10px 0" }}>
            <BlockMath math={part.slice(2, -2)} />
          </div>
        );
      }

      // \[...\] (block)
      if (part.startsWith("\\[") && part.endsWith("\\]")) {
        return (
          <div key={index} style={{ margin: "10px 0" }}>
            <BlockMath math={part.slice(2, -2)} />
          </div>
        );
      }

      // \(...\) (inline)
      if (part.startsWith("\\(") && part.endsWith("\\)")) {
        return (
          <InlineMath key={index} math={part.slice(2, -2)} />
        );
      }

      // $...$ (inline)
      if (part.startsWith("$") && part.endsWith("$")) {
        return (
          <InlineMath key={index} math={part.slice(1, -1)} />
        );
      }

      // Normal text
      return (
        <span key={index} style={{ whiteSpace: "pre-line" }}>
          {part}
        </span>
      );
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <link
        href="https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Serif+Display&display=swap"
        rel="stylesheet"
      />

      <PageWrapper>
        <ContentBox>
          {/* ── Page Header ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 4,
              animation: `${fadeUp} 0.4s ease both`,
            }}
          >
            <BackBtn
              startIcon={<ArrowBackIcon sx={{ fontSize: "15px !important" }} />}
              onClick={() => navigate(-1)}
            >
              Back
            </BackBtn>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: 22, sm: 28 },
                  color: "text.primary",
                  lineHeight: 1.1,
                }}
              >
                {editMode ? "Edit Exam Question" : "Add Exam Question"}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "text.disabled",
                  mt: 0.3,
                  fontFamily: "'Sora', sans-serif",
                }}
              >
                {testInfo ? (
                  <>
                    Level:{" "}
                    <strong style={{ color: "#4B5563" }}>
                      {testInfo.level}
                    </strong>
                    {" • "}
                    Year:{" "}
                    <strong style={{ color: "#4B5563" }}>
                      {testInfo.year}
                    </strong>
                  </>
                ) : (
                  "Loading test info..."
                )}
              </Typography>
            </Box>

            <Chip
              label={editMode ? "Editing" : "New"}
              size="small"
              sx={{
                ml: "auto",
                fontFamily: "'Sora', sans-serif",
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                background: editMode
                  ? alpha("#C4922A", 0.12)
                  : alpha("#2563EB", 0.1),
                color: editMode ? "#C4922A" : "#2563EB",
                border: `1px solid ${
                  editMode ? alpha("#C4922A", 0.25) : alpha("#2563EB", 0.2)
                }`,
              }}
            />
          </Box>

          {/* ── Upload / PDF Card ── */}
          <SectionCard delay={50}>
            <CardLabel>Import from PDF</CardLabel>
            {/* <UploadZone
              dragover={dragOver ? 1 : 0}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
              }}
              onClick={() => pdfInputRef.current?.click()}
            >
              <Box
                className="upload-icon"
                sx={{
                  transition: "transform 0.25s ease",
                  display: "inline-block",
                  mb: 1,
                }}
              >
                <CloudUploadIcon
                  sx={{ fontSize: 38, color: "primary.main", opacity: 0.7 }}
                />
              </Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: 14,
                  color: "text.primary",
                  mb: 0.4,
                  fontFamily: "'Sora', sans-serif",
                }}
              >
                Drop your exam PDF here
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "text.disabled",
                  fontFamily: "'Sora', sans-serif",
                }}
              >
                or click to browse — PDF, max 20 MB
              </Typography>
            </UploadZone> */}

            {/* Existing UploadExamObjectivePDF — kept, hidden visually but functional */}
            <Box sx={{ mt: 2 }}>
              <UploadExamObjectivePDF
                TestId={testId}
                dragOver={dragOver}
                setDragOver={setDragOver}
              />
            </Box>
          </SectionCard>

          {/* ── Question Form Card ── */}
          <SectionCard delay={100}>
            <CardLabel>Question Details</CardLabel>

            {/* Question Number */}
            <Box sx={{ mb: 2.5, maxWidth: 180 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.6px",
                  textTransform: "uppercase",
                  color: "text.disabled",
                  mb: 0.8,
                  fontFamily: "'Sora', sans-serif",
                }}
              >
                Question No.
              </Typography>
              <StyledTextField
                type="number"
                placeholder="e.g. 12"
                size="small"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                inputProps={{ min: 1 }}
                sx={{
                  "& input": {
                    fontFamily: "'Sora', sans-serif",
                    fontWeight: 700,
                    fontSize: 16,
                  },
                }}
              />
            </Box>

            {/* Question Text + Mode Toggle */}
            <Box sx={{ mb: 2.5 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.6px",
                    textTransform: "uppercase",
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                  }}
                >
                  Question Text
                </Typography>
                <ModeToggle>
                  <ModeBtn
                    active={mode === "text" ? 1 : 0}
                    startIcon={
                      <TextFieldsIcon sx={{ fontSize: "14px !important" }} />
                    }
                    onClick={() => setMode("text")}
                  >
                    Text
                  </ModeBtn>
                  <ModeBtn
                    active={mode === "math" ? 1 : 0}
                    startIcon={
                      <FunctionsIcon sx={{ fontSize: "14px !important" }} />
                    }
                    onClick={() => setMode("math")}
                  >
                    Math
                  </ModeBtn>
                </ModeToggle>
              </Box>

              {mode === "text" ? (
                <StyledTextField
                  multiline
                  minRows={3}
                  fullWidth
                  placeholder="Type your question here…"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                />
              ) : (
                <Box
                  sx={{
                    border: `1px solid ${alpha("#000", 0.1)}`,
                    borderRadius: "10px",
                    overflow: "hidden",
                    background: alpha("#F0F2F8", 0.8),
                    "&:focus-within": {
                      border: `1.5px solid #2563EB`,
                      background: "#fff",
                      boxShadow: "0 0 0 3px rgba(37,99,235,0.08)",
                    },
                  }}
                >
                  <math-field
                    ref={questionRef}
                    value={text}
                    onInput={(e) => setText(e.target.getValue())}
                    default-mode="text"
                    smart-fence
                    style={{
                      width: "100%",
                      minHeight: 100,
                      border: "none",
                      padding: 12,
                      fontSize: 16,
                      background: "transparent",
                      fontFamily: "'Sora', sans-serif",
                    }}
                  />
                </Box>
              )}
            </Box>

            {/* Image Upload */}
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.6px",
                  textTransform: "uppercase",
                  color: "text.disabled",
                  mb: 1,
                  fontFamily: "'Sora', sans-serif",
                }}
              >
                Attach Image (optional)
              </Typography>
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                flexWrap="wrap"
                gap={1}
              >
                <Button
                  component="label"
                  startIcon={<ImageIcon sx={{ fontSize: "15px !important" }} />}
                  sx={{
                    borderRadius: 2.5,
                    fontSize: 13,
                    fontWeight: 600,
                    textTransform: "none",
                    border: `1px solid ${alpha("#000", 0.12)}`,
                    color: "text.secondary",
                    background: "#fff",
                    px: 2,
                    py: 0.9,
                    fontFamily: "'Sora', sans-serif",
                    "&:hover": {
                      borderColor: "primary.main",
                      color: "primary.main",
                      background: alpha("#2563EB", 0.04),
                    },
                  }}
                >
                  Upload Image
                  <input
                    type="file"
                    ref={imageInputRef}
                    accept="image/*"
                    hidden
                    onChange={handleImageUpload}
                  />
                </Button>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                  }}
                >
                  PNG, JPG up to 10 MB
                </Typography>
                {isRemovingBg && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Spinner />
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "primary.main",
                        fontFamily: "'Sora', sans-serif",
                      }}
                    >
                      Removing background…
                    </Typography>
                  </Stack>
                )}
              </Stack>

              {imagePreview && (
                <Box
                  component="img"
                  src={imagePreview}
                  alt="Preview"
                  sx={{
                    mt: 2,
                    maxHeight: 160,
                    borderRadius: 2,
                    border: `1px solid ${alpha("#000", 0.08)}`,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    display: "block",
                  }}
                />
              )}
              {existingImage && (
                <Box
                  component="img"
                  src={existingImage}
                  alt="Existing"
                  sx={{
                    mt: 2,
                    maxHeight: 160,
                    borderRadius: 2,
                    border: `1px solid ${alpha("#000", 0.08)}`,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    display: "block",
                  }}
                  onError={(e) => (e.target.style.display = "none")}
                />
              )}
            </Box>

            {/* Submit / Cancel */}
            <Stack direction="row" spacing={1.5}>
              <PrimaryBtn
                startIcon={<CheckIcon sx={{ fontSize: "16px !important" }} />}
                disabled={!!loading}
                onClick={handleSubmit}
              >
                {loading
                  ? "Saving…"
                  : editMode
                  ? "Update Question"
                  : "Add Question"}
              </PrimaryBtn>
              {editMode && (
                <Button
                  onClick={resetForm}
                  sx={{
                    borderRadius: 2.5,
                    fontSize: 14,
                    fontWeight: 600,
                    textTransform: "none",
                    border: `1px solid ${alpha("#000", 0.12)}`,
                    color: "text.secondary",
                    px: 2.5,
                    fontFamily: "'Sora', sans-serif",
                    "&:hover": { background: alpha("#000", 0.04) },
                  }}
                >
                  Cancel
                </Button>
              )}
            </Stack>
          </SectionCard>

          {/* ── Question Bank Card ── */}
          <SectionCard delay={150}>
            {/* Header row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <CardLabel sx={{ mb: 0 }}>Question Bank</CardLabel>
              <Chip
                label={`${filteredAssigned.length} question${
                  filteredAssigned.length !== 1 ? "s" : ""
                }`}
                size="small"
                sx={{
                  fontFamily: "'Sora', sans-serif",
                  fontWeight: 700,
                  fontSize: 11,
                  background: alpha("#2563EB", 0.08),
                  color: "#2563EB",
                  border: `1px solid ${alpha("#2563EB", 0.18)}`,
                }}
              />
            </Box>

            {/* Search */}
            <StyledTextField
              fullWidth
              placeholder="Search by question number…"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Question list */}
            {paginatedQuestions.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 5 }}>
                <UploadFileIcon
                  sx={{
                    fontSize: 40,
                    color: "text.disabled",
                    opacity: 0.35,
                    mb: 1,
                    display: "block",
                    mx: "auto",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 14,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                  }}
                >
                  No questions found.
                </Typography>
              </Box>
            ) : (
              <>
                {paginatedQuestions.map((q, i) => (
                  <QuestionItem key={q.id} itemdelay={i * 50}>
                    <QNumber>{q.number}</QNumber>

                    <Box sx={{ flex: 1, pt: 0.4, minWidth: 0 }}>
                      <Typography
                        component="div"
                        sx={{
                          fontSize: 14,
                          color: "text.secondary",
                          lineHeight: 1.65,
                          fontFamily: "'Sora', sans-serif",
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        {renderContent(q.text)}
                      </Typography>
                    </Box>

                    <Stack
                      direction="row"
                      spacing={0.8}
                      sx={{
                        flexShrink: 0,
                        pt: 0.3,
                        flexWrap: "wrap",
                        gap: 0.5,
                      }}
                    >
                      <Tooltip title="Manage options" arrow>
                        <ActionBtn
                          btncolor="option"
                          onClick={() => navigateToOption(q.id)}
                        >
                          <TuneIcon sx={{ fontSize: 13, mr: 0.4 }} />
                          Options
                        </ActionBtn>
                      </Tooltip>
                      <Tooltip title="Edit question" arrow>
                        <ActionBtn
                          btncolor="edit"
                          onClick={() => startEdit(q.id)}
                        >
                          <EditIcon sx={{ fontSize: 13, mr: 0.4 }} />
                          Edit
                        </ActionBtn>
                      </Tooltip>
                      <Tooltip title="Delete question" arrow>
                        <ActionBtn
                          btncolor="delete"
                          onClick={() => deleteQuestion(q.id)}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 13, mr: 0.4 }} />
                          Delete
                        </ActionBtn>
                      </Tooltip>
                    </Stack>
                  </QuestionItem>
                ))}

                {/* Pagination */}
                {totalPages > 1 && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mt: 3,
                      pt: 2,
                      borderTop: `1px solid ${alpha("#000", 0.06)}`,
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "text.disabled",
                        fontFamily: "'Sora', sans-serif",
                      }}
                    >
                      Showing {(currentPage - 1) * QUESTIONS_PER_PAGE + 1}–
                      {Math.min(
                        currentPage * QUESTIONS_PER_PAGE,
                        filteredAssigned.length
                      )}{" "}
                      of {filteredAssigned.length}
                    </Typography>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={(_, val) => {
                        setCurrentPage(val);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      shape="rounded"
                      size="small"
                      sx={{
                        "& .MuiPaginationItem-root": {
                          fontFamily: "'Sora', sans-serif",
                          fontWeight: 600,
                          fontSize: 13,
                          borderRadius: 2,
                          border: `1px solid ${alpha("#000", 0.1)}`,
                          color: "text.secondary",
                          "&:hover": {
                            background: alpha("#2563EB", 0.06),
                            borderColor: alpha("#2563EB", 0.3),
                          },
                          "&.Mui-selected": {
                            background:
                              "linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)",
                            color: "#fff",
                            border: "none",
                            boxShadow: "0 3px 8px rgba(37,99,235,0.3)",
                            "&:hover": {
                              background:
                                "linear-gradient(135deg, #1D4ED8 0%, #1E3A8A 100%)",
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          </SectionCard>
        </ContentBox>
      </PageWrapper>
    </ThemeProvider>
  );
}
